import React, { useState } from 'react';
import styled from 'styled-components';
import { space, width } from 'styled-system';
import moment from 'moment';

import useTranslation from 'src/hooks/use-translation';
import useGeoinfo from 'src/hooks/use-geoinfo';
import useErnie from 'shared/hooks/use-ernie';
import { FormTextInput, FormFormattedInput } from 'src/components/form-inputs';
import Form from 'src/components/forms/form';
import useUser from 'src/hooks/use-user';
import { Button } from 'src/components/core';
import { Typography } from 'src/typography';
import MedicalCardForm from 'src/components/forms/sign-up-form/medical-card-form';
import { useCompleteAccount } from './use-complete-account';

type CompleteAccountProps = {
  close: () => void;
  showMedicalCardForm?: boolean;
};

export const CompleteAccount = ({ close, showMedicalCardForm = false }: CompleteAccountProps): JSX.Element => {
  const { t } = useTranslation();
  const { email, firstName, lastName, birthday, formatedBirthdate, phone } = useUser();
  const [geoinfo] = useGeoinfo();
  const country = geoinfo?.country;
  const showErnie = useErnie();
  const [medicalCardRequired, setMedicalCardRequired] = useState(false);
  const { handleSubmit, isLoading, completeAccountValidation } = useCompleteAccount({
    close,
    showMedicalCardForm: showMedicalCardForm && medicalCardRequired,
  });

  const formValues = {
    firstName,
    lastName,
    phone,
    birthday: birthday ? formatedBirthdate : null,
    email,
  };

  // Same 18-21/non-Canada age-gate logic as sign-up-form/index.jsx's ageCheck, ported so this
  // profile step collects the same legally-required medical card info SignUpForm does.
  const ageCheck = (e: React.FocusEvent<HTMLDivElement>): void => {
    if (!showMedicalCardForm || e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    const birthdayValue = (e.target as HTMLInputElement).value;
    const formattedBirthday = moment(birthdayValue, `MM/DD/YYYY`, true);

    if (!formattedBirthday.isValid()) {
      return;
    }

    const yearDiff = moment().diff(formattedBirthday, `years`);

    if (!medicalCardRequired && yearDiff >= 18 && yearDiff < 21 && country !== 'CA') {
      setMedicalCardRequired(true);
    }

    if (moment().diff(formattedBirthday, `years`) < 18) {
      showErnie(t('signUp.ageGate', 'You must be 18 years of age or older to use dutchie'), `danger`);
      setMedicalCardRequired(false);
    }

    if (medicalCardRequired && yearDiff >= 21) {
      setMedicalCardRequired(false);
    }
  };

  return (
    <>
      <Typography.Heading tag='h2' size='medium'>
        Complete your account
      </Typography.Heading>

      <SubText size='medium'>Enter the required info below to complete your account.</SubText>

      <Form
        defaultValues={formValues}
        onSubmit={handleSubmit}
        mode='onSubmit'
        validationSchema={completeAccountValidation(t, showMedicalCardForm && medicalCardRequired)}
      >
        <InlineContainer>
          <InputContainer width='50%' mr='8px'>
            <FormTextInput
              name='firstName'
              label={t('signUp.firstName', 'First Name')}
              variant='filled'
              autoComplete='given-name'
              required
              autoFocus
            />
          </InputContainer>

          <InputContainer width='50%' ml='8px'>
            <FormTextInput
              name='lastName'
              label={t('signUp.lastName', 'Last Name')}
              variant='filled'
              autoComplete='family-name'
              required
            />
          </InputContainer>
        </InlineContainer>

        <InputContainer mt={16}>
          <FormFormattedInput
            format='phone'
            name='phone'
            label={t('signUp.phone', 'Mobile Phone Number')}
            variant='filled'
            autoComplete='tel'
            required
          />
        </InputContainer>

        <InputContainer onBlur={ageCheck} mt={16}>
          <FormFormattedInput
            format='date'
            name='birthday'
            label={t('signUp.birthday', 'Birthday')}
            variant='filled'
            autoComplete='bday'
            required
          />
        </InputContainer>

        <InputContainer mt={16}>
          <FormTextInput
            name='email'
            label={t('signUp.email', 'Email Address')}
            variant='filled'
            autoComplete='email'
            type='email'
            disabled
          />
        </InputContainer>

        {showMedicalCardForm && medicalCardRequired && <MedicalCardForm />}

        <ButtonContainer>
          <Button type='submit' mt={16} loading={isLoading} data-testid='complete-account-submit-button'>
            Save
          </Button>
        </ButtonContainer>
      </Form>
    </>
  );
};

const SubText = styled(Typography.Body)`
  color: ${({ theme }) => theme.colors.grey[30]};
  margin: 24px 0;
`;

const InputContainer = styled.div`
  ${width}
  ${space}
`;

const InlineContainer = styled.div`
  display: flex;
  ${space}
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;
